import {
  Invitation,
  SignUpForm,
  useInvitation,
  useZapTemplate,
} from '@zapier/authentication';
import { ReactNode, useEffect } from 'react';
import { NextRouter, useRouter } from 'next/router';

import {
  isNLAPluginSignUp,
  resolveSignUpNextUrl,
} from '@zapier/identity-utils';

import { AuthLayout, InvitationError } from '../../global';
import { useNotification } from '../../global/Notifications';
import { TEAM_INVITATION_STATUS_MISMATCHED_EMAIL } from '../../../constants';
import { useIsWorkflowAPI } from '../../../utils/useIsWorkflowAPI';
import { useIsClaudeMCPReferrer } from '../../../utils/isClaudeMCPReferrer';
import { WorkflowAPIHeader } from '../../global/WorkflowAPIHeader';

import { getZapTemplateId } from './utils';
import { SignUpPromo } from './SignUpPromo';
import { ZapTemplatePromo } from './ZapTemplatePromo';
import { ZapTemplateTitle } from './ZapTemplateTitle';
import { InvitationPromo } from './InvitationPromo';
import { NLASignUpPromo } from './NLASignUpPromo';
import { ClaudeSignUpPromo } from './ClaudeSignUpPromo';

import { Section, SectionProps } from '@zapier/builder-io-kit-compat';
import { DiscoveredDomainAccount } from '@zapier/authentication/src/utils/discoverDomain';
import { Metadata, PrefillData } from '@zapier/authentication/src/types';

export type SignupPageProps = {
  builderSections?: {
    leftSide: SectionProps['content'] | null;
    footer: SectionProps['content'] | null;
  };
  invitationData?: {
    isValid: boolean;
    invitation?: Invitation;
    error?: string;
    code?: string;
  };
  domainCaptureData?: {
    isFromSsoReturn: boolean;
    errorModalMessage?: string | null;
    discoveredDomainAccount?: DiscoveredDomainAccount | null;
  };
  metadata?: Metadata;
  prefillData?: PrefillData;
};

export function SignupPage({
  builderSections,
  invitationData,
  domainCaptureData,
  metadata,
  prefillData,
}: SignupPageProps) {
  const invitation = invitationData?.invitation;
  const code = invitationData?.code;
  const router = useRouter();

  const isNLARedirect = isNLAPluginSignUp(router.query.utm_campaign);
  const isWorkflowAPI = useIsWorkflowAPI();

  const urlSearchParams = new URLSearchParams(window.location.search);
  const selectedApis = urlSearchParams.get('selected_apis') || undefined;

  const next = resolveSignUpNextUrl(
    router.query.next as string | undefined,
    { selected_apis: selectedApis },
    { skipOnboarding: router.query.skipOnboarding || isNLARedirect }
  );

  const ztId = getZapTemplateId(router);
  const zapTemplate = useZapTemplate(ztId);
  const isClaudeReferrer = useIsClaudeMCPReferrer();
  const { createNotification } = useNotification();
  const { declineInvitation } = useInvitation(code, {
    skip: true,
  });

  useEffect(() => {
    if (
      router.isReady &&
      router.query.notification_code === TEAM_INVITATION_STATUS_MISMATCHED_EMAIL
    ) {
      createNotification({
        title: "Email doesn't match invite",
        message:
          "The email you've used to sign up doesn't match the email that was invited to the account. Please try again.",
        type: 'error',
      });
    }
  }, [router.isReady, createNotification, router.query.notification_code]);

  if (invitationData && (invitationData?.error || !invitationData?.isValid)) {
    return (
      <InvitationError error={invitationData?.error} url={router.asPath} />
    );
  }

  let title: ReactNode = null;
  if (isWorkflowAPI) {
    title = 'Continue automating with a Zapier account';
  } else if (zapTemplate?.zapTemplate && !builderSections?.leftSide) {
    title = <ZapTemplateTitle zapTemplate={zapTemplate.zapTemplate} />;
  }

  async function declineInvitationAndNavigate() {
    if (code) {
      await declineInvitation(code);
      router.push(`/app/invitation/${code}/decline`);
    }
  }

  const builderSectionData = {
    invitation,
    zapTemplate: zapTemplate?.zapTemplate || {},
    declineInvitationAndNavigate,
  };

  function getPromo() {
    // Now that the promo is based on url params, the router must be ready
    // before we can display anything
    // Optional, display loading for SSR? I don't think users will
    // notice anything here
    if (!router.isReady) {
      return null;
    }

    if (isClaudeReferrer) {
      return <ClaudeSignUpPromo />;
    } else if (builderSections?.leftSide) {
      return (
        <Section
          content={builderSections.leftSide}
          serviceName="identity"
          data={builderSectionData}
        />
      );
    } else if (zapTemplate.zapTemplate) {
      return <ZapTemplatePromo zapTemplate={zapTemplate.zapTemplate} />;
    } else if (invitation) {
      return (
        <InvitationPromo
          invitation={invitation}
          handleDeclineButtonClick={declineInvitationAndNavigate}
        />
      );
    } else if (isNLARedirect) {
      return <NLASignUpPromo />;
    } else {
      return <SignUpPromo />;
    }
  }
  const promo = getPromo();

  const footer =
    builderSections?.footer && !isWorkflowAPI ? (
      <Section
        content={builderSections.footer}
        serviceName="identity"
        data={builderSectionData}
      />
    ) : null;

  return (
    <AuthLayout
      footer={footer}
      promo={promo}
      showPromo={!isWorkflowAPI}
      title={title}
      header={isWorkflowAPI ? <WorkflowAPIHeader /> : null}
    >
      <SignUpForm
        enableEmailAutoFocus={true}
        googleCaptchaSiteKey={
          process.env.NEXT_PUBLIC_GOOGLE_CAPTCHA_SITE_KEY || ''
        }
        invitation={invitationData?.isValid ? invitationData?.invitation : null}
        next={next}
        queryParams={router.query}
        selectedApis={selectedApis}
        routeTo={router.push}
        useCase={
          (router.query.use_case || router.query.use_case_slug) as string
        }
        from={getReferrer(router.query)}
        domainCaptureData={domainCaptureData}
        metadata={metadata}
        prefillData={prefillData}
      />
    </AuthLayout>
  );
}

// Get `?from` or `?referer` or `?referrer` from the URL params or from document and use that as
// the real referrer value if any are present, in that order.
function getReferrer(query: NextRouter['query']): string | undefined {
  // Various systems at Zapier use different URL params for referrer tracking,
  // so let's attempt to support all of them
  const value =
    query.from || query.referer || query.referrer || document.referrer;
  // Get last item if there are duplicate query parameters
  if (Array.isArray(value)) {
    return value[value.length - 1];
  }
  return value;
}
